import "dayjs/locale/id";
import dayjs from "dayjs";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { en } from "./en";
import { id, type MessageKey } from "./id";

export type Locale = "id" | "en";
export type { MessageKey };

const dictionaries: Record<Locale, Record<MessageKey, string>> = { id, en };
const STORAGE_KEY = "zerpid.locale";

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

type I18nValue = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
  /** Translate a key that arrives at runtime (error codes, activity actions). */
  tDynamic: (key: string, fallback: MessageKey, vars?: Record<string, string | number>) => string;
  formatDateTime: (iso: string, timeZone?: string) => string;
  formatDate: (iso: string, timeZone?: string) => string;
  /** Whole-unit amounts as the marketplace sends them (rupiah has no minor unit). */
  formatMoney: (amount: number, currency?: string) => string;
  formatNumber: (n: number) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

function initialLocale(): Locale {
  // zerpid.com links to sign-up with ?lang= so the form opens in the page's language.
  const fromLink = new URLSearchParams(window.location.search).get("lang");
  if (fromLink === "id" || fromLink === "en") return fromLink;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "id" || saved === "en") return saved;
  } catch {
    // storage blocked: fall through
  }
  return navigator.language.toLowerCase().startsWith("en") ? "en" : "id";
}

function interpolate(template: string, vars?: Record<string, string | number>) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // not fatal
    }
    document.documentElement.lang = l;
    dayjs.locale(l);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dict = dictionaries[locale];
    const intlLocale = locale === "id" ? "id-ID" : "en-GB";
    const t: Translate = (key, vars) => interpolate(dict[key], vars);
    return {
      locale,
      setLocale,
      t,
      tDynamic: (key, fallback, vars) =>
        interpolate((dict as Record<string, string>)[key] ?? dict[fallback], vars),
      formatDateTime: (iso, timeZone = "Asia/Jakarta") =>
        new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(iso)),
      formatDate: (iso, timeZone = "Asia/Jakarta") =>
        new Intl.DateTimeFormat(intlLocale, { dateStyle: "long", timeZone }).format(new Date(iso)),
      formatMoney: (amount, currency = "IDR") =>
        new Intl.NumberFormat(intlLocale, { style: "currency", currency: currency || "IDR", maximumFractionDigits: 0 }).format(amount),
      formatNumber: (n) => new Intl.NumberFormat(intlLocale).format(n),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error("useI18n outside I18nProvider");
  return v;
}
